import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Print as PrintIcon,
  Storage as MetadataIcon,
  FileDownload as ExportIcon,
  Save as SaveIcon,
  Close as CancelIcon,
  Comment as CommentIcon,
  Biotech as FindingsIcon,
} from '@mui/icons-material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { projectAPI, labelAPI, specimenAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { formatProjectLabel } from '../../utils/formatProjectLabel';
import { downloadAsFile } from '../../utils/downloadAsFile';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { canEditLabData, isAdmin as checkIsAdmin } from '../../utils/roleUtils';
import ExportDialog from '../common/ExportDialog';
import DetailBreadcrumbs from '../common/DetailBreadcrumbs';
import SystemOptionAutocomplete from '../common/SystemOptionAutocomplete';
import ProjectFindingsSection from '../analysis/ProjectFindingsSection';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [specimens, setSpecimens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [specimensLoading, setSpecimensLoading] = useState(true);
  const [error, setError] = useState('');
  const [metadataFields, setMetadataFields] = useState([]);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [editingComments, setEditingComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [savingComments, setSavingComments] = useState(false);
  // Specimen selection and bulk comment state
  const [selectedSpecimens, setSelectedSpecimens] = useState([]);
  const [bulkCommentDialogOpen, setBulkCommentDialogOpen] = useState(false);
  const [bulkCommentText, setBulkCommentText] = useState('');
  const [bulkCommentLoading, setBulkCommentLoading] = useState(false);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const [bulkEditEnabled, setBulkEditEnabled] = useState({
    position_freezer: false,
    position_rack: false,
    position_box: false,
    activity_status: false,
    used_up: false,
    extracted: false,
  });
  const [bulkEditValues, setBulkEditValues] = useState({
    position_freezer: '',
    position_rack: '',
    position_box: '',
    activity_status: '',
    used_up: false,
    extracted: false,
  });
  const navigate = useNavigate();

  // Memoize comment preview calculations to avoid recalculating on every keystroke
  const { selectedWithComments, selectedWithoutComments } = useMemo(() => {
    const hasRealComment = (comment) => {
      return comment && comment.trim() && comment.trim().toUpperCase() !== 'NULL';
    };

    return {
      selectedWithComments: specimens.filter(
        s => selectedSpecimens.includes(s.id) && hasRealComment(s.comments)
      ),
      selectedWithoutComments: specimens.filter(
        s => selectedSpecimens.includes(s.id) && !hasRealComment(s.comments)
      )
    };
  }, [specimens, selectedSpecimens]);
  const { currentUser } = useAuth();
  const isAdmin = checkIsAdmin(currentUser);
  const canEdit = canEditLabData(currentUser);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await projectAPI.getById(id);
        setProject(response.data);
      } catch (err) {
        console.error('Error fetching project', err);
        setError('Failed to load project details');
      } finally {
        setLoading(false);
      }
    };

    const fetchSpecimens = async () => {
      try {
        const response = await projectAPI.getSpecimens(id);
        setSpecimens(response.data);
      } catch (err) {
        console.error('Error fetching specimens', err);
        // Don't set error for specimens, just log it
      } finally {
        setSpecimensLoading(false);
      }
    };

    fetchProject();
    fetchSpecimens();
    fetchProjectMetadata();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/id-change fetch only
  }, [id]);

  const fetchProjectMetadata = async () => {
    try {
      setMetadataLoading(true);
      const response = await specimenAPI.getAll(`?project_id=${id}`);
      const projectSpecimens = response.data.specimens || response.data;
      
      // Extract all unique metadata fields from specimens in this project
      const allMetadataFields = new Set();
      const specimenMetadata = [];
      
      projectSpecimens.forEach(specimen => {
        if (specimen.metadata && typeof specimen.metadata === 'object') {
          Object.keys(specimen.metadata).forEach(field => {
            allMetadataFields.add(field);
          });
          specimenMetadata.push({
            specimenId: specimen.tube_id || specimen.id,
            metadata: specimen.metadata
          });
        }
      });
      
      setMetadataFields([...allMetadataFields]);
    } catch (err) {
      console.error('Error fetching project metadata', err);
    } finally {
      setMetadataLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this project? This will also delete all associated specimens.')) {
      return;
    }

    try {
      await projectAPI.delete(id);
      toast.success('Project deleted successfully');
      navigate('/projects');
    } catch (err) {
      console.error('Error deleting project', err);
      toast.error('Failed to delete project');
    }
  };

  const handleGenerateLabels = async () => {
    try {
      // Selection-aware: when specimens are selected, label only those;
      // otherwise label the whole project.
      const response = selectedSpecimens.length > 0
        ? await labelAPI.generateLabels(selectedSpecimens)
        : await labelAPI.generateProjectLabels(id);
      if (response.data && response.data.filename) {
        const downloadResponse = await labelAPI.downloadLabel(response.data.filename);
        downloadAsFile(downloadResponse.data, response.data.filename);
        toast.success(`Labels downloaded: ${response.data.filename}`);
      } else {
        toast.error('No file generated');
      }
    } catch (err) {
      console.error('Error generating labels', err);
      toast.error('Failed to generate labels');
    }
  };

  const handleEditComments = () => {
    setCommentText(project.comments || '');
    setEditingComments(true);
  };

  const handleCancelEditComments = () => {
    setEditingComments(false);
    setCommentText('');
  };

  const handleSaveComments = async () => {
    setSavingComments(true);
    try {
      await projectAPI.update(id, { ...project, comments: commentText });
      setProject({ ...project, comments: commentText });
      setEditingComments(false);
      toast.success('Comments saved successfully');
    } catch (err) {
      console.error('Error saving comments', err);
      toast.error('Failed to save comments');
    } finally {
      setSavingComments(false);
    }
  };

  // Specimen selection handlers
  const handleSelectSpecimen = (specimenId) => {
    setSelectedSpecimens((prev) => {
      if (prev.includes(specimenId)) {
        return prev.filter((id) => id !== specimenId);
      } else {
        return [...prev, specimenId];
      }
    });
  };

  const handleSelectAllSpecimens = () => {
    if (selectedSpecimens.length === specimens.length) {
      setSelectedSpecimens([]);
    } else {
      setSelectedSpecimens(specimens.map((s) => s.id));
    }
  };

  // Bulk comment handler
  const handleBulkCommentSubmit = async () => {
    if (!bulkCommentText.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    if (selectedSpecimens.length === 0) {
      toast.error('Please select at least one specimen');
      return;
    }

    setBulkCommentLoading(true);
    try {
      const response = await specimenAPI.bulkComment(
        selectedSpecimens,
        bulkCommentText.trim(),
        'append'
      );

      toast.success(response.data.msg || `Comment added to ${response.data.updated_count} specimens`);

      // Reset state
      setBulkCommentDialogOpen(false);
      setBulkCommentText('');
      setSelectedSpecimens([]);

      // Refresh specimens list
      const specimensResponse = await projectAPI.getSpecimens(id);
      setSpecimens(specimensResponse.data);
    } catch (err) {
      console.error('Error adding bulk comment:', err);
      toast.error(err.response?.data?.msg || 'Failed to add comments');
    } finally {
      setBulkCommentLoading(false);
    }
  };

  const resetBulkEditState = () => {
    setBulkEditEnabled({
      position_freezer: false,
      position_rack: false,
      position_box: false,
      activity_status: false,
      used_up: false,
      extracted: false,
    });
    setBulkEditValues({
      position_freezer: '',
      position_rack: '',
      position_box: '',
      activity_status: '',
      used_up: false,
      extracted: false,
    });
  };

  const handleBulkEditSubmit = async () => {
    if (selectedSpecimens.length === 0) {
      toast.error('Please select at least one specimen');
      return;
    }

    const updates = {};
    Object.keys(bulkEditEnabled).forEach((field) => {
      if (bulkEditEnabled[field]) {
        updates[field] = bulkEditValues[field];
      }
    });

    if (Object.keys(updates).length === 0) {
      toast.error('Enable at least one field to update');
      return;
    }

    setBulkEditLoading(true);
    try {
      const response = await specimenAPI.bulkUpdate(selectedSpecimens, updates);
      toast.success(
        response.data.msg || `Updated ${response.data.updated_count} specimens`
      );
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warn(`${response.data.errors.length} specimen(s) failed to update`);
      }
      setBulkEditDialogOpen(false);
      resetBulkEditState();
      setSelectedSpecimens([]);

      const specimensResponse = await projectAPI.getSpecimens(id);
      setSpecimens(specimensResponse.data);
    } catch (err) {
      console.error('Error bulk-updating specimens:', err);
      toast.error(err.response?.data?.msg || 'Failed to bulk-update specimens');
    } finally {
      setBulkEditLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box my={4}>
        <Alert severity="error">{error}</Alert>
        <Button
          component={Link}
          to="/projects"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box my={4}>
        <Alert severity="info">Project not found</Alert>
        <Button
          component={Link}
          to="/projects"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  return (
    <Box className="project-detail page-container">
      <DetailBreadcrumbs
        items={[
          { label: 'Projects', to: '/projects' },
          project.collaborator_id && project.pi_name
            ? { label: project.pi_name, to: `/collaborators/${project.collaborator_id}` }
            : null,
          { label: formatProjectLabel(project) },
        ]}
      />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {formatProjectLabel(project, { variant: 'title' })}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={() => setExportDialogOpen(true)}
            sx={{ mr: 1 }}
            disabled={specimens.length === 0}
          >
            Export Specimens
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handleGenerateLabels}
            sx={{ mr: 1 }}
          >
            {selectedSpecimens.length > 0
              ? `Print Labels (${selectedSpecimens.length})`
              : 'Print Labels'}
          </Button>
          {canEdit && (
            <>
              <Button
                component={Link}
                to={`/projects/edit/${id}`}
                variant="outlined"
                startIcon={<EditIcon />}
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
              {isAdmin && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Project Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Project Number</Typography>
                <Typography variant="body1" gutterBottom>
                  {project.project_number === 0
                    ? 'Unknown (#0)'
                    : project.project_number != null
                      ? `#${project.project_number}`
                      : '—'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Collaborator</Typography>
                <Typography variant="body1" gutterBottom>
                  <Link to={`/collaborators/${project.collaborator_id}`}>
                    {project.pi_name && project.pi_institute ? 
                      `${project.pi_name}, ${project.pi_institute}` : 
                      'Unknown Collaborator'}
                  </Link>
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Study</Typography>
                <Typography variant="body1" gutterBottom>
                  {project.study_id ? (
                    <Link to={`/studies/${project.study_id}`}>{project.study_name}</Link>
                  ) : (
                    '—'
                  )}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Disease</Typography>
                <Typography variant="body1" gutterBottom>{project.disease || '—'}</Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Specimen Type</Typography>
                <Typography variant="body1" gutterBottom>{project.specimen_type || '—'}</Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Source</Typography>
                <Typography variant="body1" gutterBottom>{project.source || '—'}</Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Date Received</Typography>
                <Typography variant="body1" gutterBottom>{formatDate(project.date_received) || '—'}</Typography>
              </Grid>
              
              {project.feedback_date && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Feedback Date</Typography>
                  <Typography variant="body1" gutterBottom>{formatDate(project.feedback_date)}</Typography>
                </Grid>
              )}
            </Grid>

            {/* Comments Section */}
            <Box mt={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CommentIcon fontSize="small" />
                  Comments
                </Typography>
                {canEdit && !editingComments && (
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={handleEditComments}
                  >
                    {project.comments ? 'Edit' : 'Add Comment'}
                  </Button>
                )}
              </Box>

              {editingComments ? (
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Enter comments..."
                    variant="outlined"
                    size="small"
                    disabled={savingComments}
                  />
                  <Box mt={1} display="flex" gap={1}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveComments}
                      disabled={savingComments}
                    >
                      {savingComments ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelEditComments}
                      disabled={savingComments}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color={project.comments ? 'text.primary' : 'text.secondary'}>
                  {project.comments || 'No comments'}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Specimens
              </Typography>
              {canEdit && (
                <Button
                  component={Link}
                  to="/specimens/new"
                  state={{ project_id: id }}
                  size="small"
                  startIcon={<AddIcon />}
                >
                  New Specimen
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Selected specimens action bar */}
            {selectedSpecimens.length > 0 && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  bgcolor: 'primary.50',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {selectedSpecimens.length} specimen{selectedSpecimens.length !== 1 ? 's' : ''} selected
                </Typography>
                {canEdit && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CommentIcon />}
                    onClick={() => setBulkCommentDialogOpen(true)}
                  >
                    Add Comment
                  </Button>
                )}
                {canEdit && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setBulkEditDialogOpen(true)}
                  >
                    Bulk Edit
                  </Button>
                )}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setSelectedSpecimens([])}
                >
                  Clear Selection
                </Button>
              </Box>
            )}
            
            {specimensLoading ? (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress size={24} />
              </Box>
            ) : specimens.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No specimens for this project
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedSpecimens.length > 0 && selectedSpecimens.length < specimens.length}
                          checked={specimens.length > 0 && selectedSpecimens.length === specimens.length}
                          onChange={handleSelectAllSpecimens}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>Specimen ID</TableCell>
                      <TableCell>Tube ID</TableCell>
                      <TableCell>Patient</TableCell>
                      <TableCell>Date Collected</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {specimens.map((specimen) => (
                      <TableRow
                        key={specimen.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/specimens/${specimen.id}`)}
                        selected={selectedSpecimens.includes(specimen.id)}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedSpecimens.includes(specimen.id)}
                            onChange={() => handleSelectSpecimen(specimen.id)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{specimen.specimen_number || '—'}</TableCell>
                        <TableCell>{specimen.tube_id || '—'}</TableCell>
                        <TableCell>
                          {specimen.patient_external_id ?
                            (specimen.patient_name ?
                              `${specimen.patient_external_id} (${specimen.patient_name})` :
                              specimen.patient_external_id) :
                            '—'}
                        </TableCell>
                        <TableCell>{formatDate(specimen.date_collected) || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Project Metadata Section */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MetadataIcon />
                Project Metadata
              </Typography>
              {canEdit && (
                <Button
                  variant="outlined"
                  startIcon={<MetadataIcon />}
                  onClick={() => navigate('/metadata')}
                  size="small"
                >
                  Manage Metadata
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {metadataLoading ? (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress size={24} />
              </Box>
            ) : metadataFields.length === 0 ? (
              <Alert severity="info">
                <Typography variant="body2">
                  No metadata fields found for specimens in this project. 
                  {canEdit && ' Use the "Manage Metadata" button to access the metadata management system.'}
                </Typography>
              </Alert>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  This project contains <strong>{metadataFields.length}</strong> metadata fields across its specimens:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {metadataFields.map((field) => (
                    <Chip
                      key={field}
                      label={field}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Contextual metadata viewing:</strong> All specimens in this project share the same metadata schema, 
                    so there are no empty columns. For detailed metadata viewing, visit individual specimen pages 
                    or use the specimens table above.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Analysis Findings Section (#52) */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
            >
              <FindingsIcon />
              Analysis Findings
            </Typography>
            <ProjectFindingsSection projectId={id} />
          </Paper>
        </Grid>
      </Grid>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        initialFilters={{ projectId: id }}
      />

      {/* Bulk Comment Dialog */}
      <Dialog
        open={bulkCommentDialogOpen}
        onClose={() => !bulkCommentLoading && setBulkCommentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CommentIcon color="primary" />
          Add Comment to {selectedSpecimens.length} Specimen{selectedSpecimens.length !== 1 ? 's' : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Show existing comments preview */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{selectedWithComments.length}</strong> specimen{selectedWithComments.length !== 1 ? 's have' : ' has'} existing comments,
                <strong> {selectedWithoutComments.length}</strong> {selectedWithoutComments.length !== 1 ? 'have' : 'has'} none
              </Typography>

              {selectedWithComments.length > 0 && (
                <Box
                  sx={{
                    maxHeight: 150,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mt: 1,
                    p: 1,
                    bgcolor: 'grey.50'
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Existing comments:
                  </Typography>
                  {selectedWithComments.slice(0, 10).map(specimen => {
                    const displayComment = specimen.comments?.trim();
                    return (
                      <Box key={specimen.id} sx={{ mb: 1, pb: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { mb: 0, pb: 0, border: 'none' } }}>
                        <Typography variant="caption" fontWeight="bold">
                          {specimen.tube_id || specimen.specimen_number || specimen.id}:
                        </Typography>
                        <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                          {displayComment.length > 100 ? displayComment.substring(0, 100) + '...' : displayComment}
                        </Typography>
                      </Box>
                    );
                  })}
                  {selectedWithComments.length > 10 && (
                    <Typography variant="caption" color="text.secondary" fontStyle="italic">
                      ...and {selectedWithComments.length - 10} more with comments
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            <TextField
              fullWidth
              multiline
              rows={4}
              value={bulkCommentText}
              onChange={(e) => setBulkCommentText(e.target.value)}
              placeholder="Enter comment to add to selected specimens..."
              variant="outlined"
              disabled={bulkCommentLoading}
              autoFocus
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This comment will be appended to any existing comments.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkCommentDialogOpen(false)}
            disabled={bulkCommentLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkCommentSubmit}
            variant="contained"
            disabled={bulkCommentLoading || !bulkCommentText.trim()}
            startIcon={bulkCommentLoading ? <CircularProgress size={16} /> : <CommentIcon />}
          >
            {bulkCommentLoading ? 'Adding...' : 'Add Comment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog
        open={bulkEditDialogOpen}
        onClose={() => !bulkEditLoading && setBulkEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          Bulk Edit {selectedSpecimens.length} Specimen{selectedSpecimens.length !== 1 ? 's' : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Enable a field to set its value on every selected specimen. Disabled fields are left unchanged.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bulkEditEnabled.position_freezer}
                    onChange={(e) =>
                      setBulkEditEnabled((s) => ({ ...s, position_freezer: e.target.checked }))
                    }
                  />
                }
                label="Freezer"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="New Freezer"
                value={bulkEditValues.position_freezer}
                onChange={(e) =>
                  setBulkEditValues((s) => ({ ...s, position_freezer: e.target.value }))
                }
                disabled={!bulkEditEnabled.position_freezer}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bulkEditEnabled.position_rack}
                    onChange={(e) =>
                      setBulkEditEnabled((s) => ({ ...s, position_rack: e.target.checked }))
                    }
                  />
                }
                label="Rack"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="New Rack"
                value={bulkEditValues.position_rack}
                onChange={(e) =>
                  setBulkEditValues((s) => ({ ...s, position_rack: e.target.value }))
                }
                disabled={!bulkEditEnabled.position_rack}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bulkEditEnabled.position_box}
                    onChange={(e) =>
                      setBulkEditEnabled((s) => ({ ...s, position_box: e.target.checked }))
                    }
                  />
                }
                label="Box"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="New Box"
                value={bulkEditValues.position_box}
                onChange={(e) =>
                  setBulkEditValues((s) => ({ ...s, position_box: e.target.value }))
                }
                disabled={!bulkEditEnabled.position_box}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bulkEditEnabled.activity_status}
                    onChange={(e) =>
                      setBulkEditEnabled((s) => ({ ...s, activity_status: e.target.checked }))
                    }
                  />
                }
                label="Activity Status"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <SystemOptionAutocomplete
                category="activity_status"
                name="activity_status"
                label="New Activity Status"
                value={bulkEditValues.activity_status}
                onChange={(e) =>
                  setBulkEditValues((s) => ({ ...s, activity_status: e.target.value }))
                }
                disabled={!bulkEditEnabled.activity_status}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bulkEditEnabled.extracted}
                    onChange={(e) =>
                      setBulkEditEnabled((s) => ({ ...s, extracted: e.target.checked }))
                    }
                  />
                }
                label="Extracted"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <FormControlLabel
                control={
                  <Switch
                    checked={bulkEditValues.extracted}
                    onChange={(e) =>
                      setBulkEditValues((s) => ({ ...s, extracted: e.target.checked }))
                    }
                    disabled={!bulkEditEnabled.extracted}
                  />
                }
                label={bulkEditValues.extracted ? 'Yes' : 'No'}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bulkEditEnabled.used_up}
                    onChange={(e) =>
                      setBulkEditEnabled((s) => ({ ...s, used_up: e.target.checked }))
                    }
                  />
                }
                label="Used Up"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <FormControlLabel
                control={
                  <Switch
                    checked={bulkEditValues.used_up}
                    onChange={(e) =>
                      setBulkEditValues((s) => ({ ...s, used_up: e.target.checked }))
                    }
                    disabled={!bulkEditEnabled.used_up}
                  />
                }
                label={bulkEditValues.used_up ? 'Yes' : 'No'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkEditDialogOpen(false)}
            disabled={bulkEditLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkEditSubmit}
            variant="contained"
            disabled={
              bulkEditLoading ||
              !Object.values(bulkEditEnabled).some(Boolean)
            }
            startIcon={bulkEditLoading ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {bulkEditLoading
              ? 'Updating...'
              : `Update ${selectedSpecimens.length} Specimen${selectedSpecimens.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ProjectDetail;